import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Sparkles } from "lucide-react";

import AirportAutocomplete from "../../components/AirportAutocomplete";
import PhotoPicker from "../../components/PhotoPicker";
import GalleryPicker from "../../components/GalleryPicker";
import ImagePositioner from "../../components/ImagePositioner";
import ToggleButtonGroup from "../../components/ToggleButtonGroup";
import RegionPills from "../../components/RegionPills";
import ShareSheet from "../../components/ShareSheet";
import RichTextEditor from "../../components/RichTextEditor";
import { CURRENCY_LIST } from "../../lib/currencies";

// Region lookup mirrors /app/backend/regions.py so we can preview tagging.
const REGION_MAP = {
  "netherlands": "Europe", "france": "Europe", "germany": "Europe", "spain": "Europe",
  "italy": "Europe", "united kingdom": "Europe", "portugal": "Europe", "belgium": "Europe",
  "switzerland": "Europe", "austria": "Europe", "sweden": "Europe", "norway": "Europe",
  "denmark": "Europe", "finland": "Europe", "iceland": "Europe", "ireland": "Europe",
  "poland": "Europe", "czech republic": "Europe", "czechia": "Europe", "hungary": "Europe",
  "greece": "Europe", "croatia": "Europe", "romania": "Europe", "bulgaria": "Europe",
  "serbia": "Europe", "slovenia": "Europe", "slovakia": "Europe", "estonia": "Europe",
  "latvia": "Europe", "lithuania": "Europe", "malta": "Europe", "luxembourg": "Europe",
  "turkey": "Europe",
  "thailand": "Asia", "japan": "Asia", "china": "Asia", "hong kong": "Asia", "macau": "Asia",
  "south korea": "Asia", "singapore": "Asia", "malaysia": "Asia", "indonesia": "Asia",
  "vietnam": "Asia", "philippines": "Asia", "india": "Asia", "sri lanka": "Asia",
  "nepal": "Asia", "cambodia": "Asia", "laos": "Asia", "myanmar": "Asia", "taiwan": "Asia",
  "mongolia": "Asia", "maldives": "Asia", "pakistan": "Asia", "bangladesh": "Asia",
  "united states": "Americas", "canada": "Americas", "mexico": "Americas", "brazil": "Americas",
  "argentina": "Americas", "chile": "Americas", "colombia": "Americas", "peru": "Americas",
  "ecuador": "Americas", "uruguay": "Americas", "bolivia": "Americas", "venezuela": "Americas",
  "cuba": "Americas", "dominican republic": "Americas", "jamaica": "Americas",
  "costa rica": "Americas", "panama": "Americas", "guatemala": "Americas",
  "south africa": "Africa", "morocco": "Africa", "egypt": "Africa", "tunisia": "Africa",
  "kenya": "Africa", "tanzania": "Africa", "ethiopia": "Africa", "nigeria": "Africa",
  "ghana": "Africa", "senegal": "Africa", "namibia": "Africa", "botswana": "Africa",
  "mauritius": "Africa", "seychelles": "Africa", "madagascar": "Africa", "zimbabwe": "Africa",
  "united arab emirates": "Middle East", "saudi arabia": "Middle East", "qatar": "Middle East",
  "oman": "Middle East", "bahrain": "Middle East", "kuwait": "Middle East", "israel": "Middle East",
  "jordan": "Middle East", "lebanon": "Middle East", "iran": "Middle East",
  "australia": "Oceania", "new zealand": "Oceania", "fiji": "Oceania",
  "french polynesia": "Oceania", "samoa": "Oceania",
};

function regionForCountry(country) {
  if (!country) return null;
  return REGION_MAP[country.trim().toLowerCase()] || null;
}

const BLANK = {
  from_city: "", from_country: "", from_airport: "",
  to_city: "", to_country: "", to_airport: "",
  return_city: "",
  return_from_city: "", return_from_country: "", return_from_airport: "",
  return_to_city: "", return_to_country: "", return_to_airport: "",
  trip_type: "round_trip",
  deal_type: "flight",
  price_eur: "", price_currency: "EUR",
  airline: "",
  travel_window: "", example_dates: "",
  stops: "nonstop",
  baggage: "cabin only",
  cabin_class: "economy",
  deal_url: "", hotel_url: "",
  image_url: "", photo_credit: "", photo_position: "", gallery: [],
  teaser: "", description: "",
  region: "",
  published: true,
  // hotel
  hotel_name: "", star_rating: "4", board_type: "Room only",
  price_per_night: "", checkin_period: "", price_per_person: "",
  // cruise
  cruise_line: "", ship_name: "", departure_port: "",
  destination_port: "", cruise_route: "", duration_nights: "", cabin_type: "Inside",
  // package
  package_includes: "Flight+Hotel",
};

export default function AdminDealForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [f, setF] = useState(() => {
    const prefill = !id ? location.state?.prefill : null;
    return prefill ? { ...BLANK, ...prefill } : BLANK;
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [aiLoading, setAiLoading] = useState(false);
  const [postPublish, setPostPublish] = useState(null);
  const [sendNewsletter, setSendNewsletter] = useState(false);
  const [activeTab, setActiveTab] = useState("flight");
  const [openJaw, setOpenJaw] = useState(false);
  const [subStats, setSubStats] = useState({ confirmed: 0 });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showShare, setShowShare] = useState(false);

  async function openServerEmailPreview(dealId) {
    try {
      const res = await api.get(`/admin/newsletter/preview/${dealId}`, { responseType: "text" });
      const blob = new Blob([res.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const tab = window.open(url, "_blank");
      if (tab) tab.addEventListener("unload", () => URL.revokeObjectURL(url), { once: true });
    } catch {
      toast.error("Could not load email preview");
    }
  }

  useEffect(() => {
    api.get("/admin/subscribers/stats").then((r) => setSubStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get("/admin/deals").then((r) => {
      const d = r.data.find((x) => x.id === id);
      if (d) {
        const REGIONS = ["Europe", "Asia", "Americas", "Africa", "Middle East", "Oceania"];
        const region = (d.tags || []).find((t) => REGIONS.includes(t)) || "";
        const trimCity = (s) => (s || "").split("(")[0].trim();
        setF({
          ...BLANK, ...d, region,
          from_city: trimCity(d.from_city),
          to_city: trimCity(d.to_city),
          return_from_city: trimCity(d.return_from_city),
          return_to_city: trimCity(d.return_to_city),
        });
        setActiveTab(d.deal_type || "flight");
        if (d.return_from_city || d.return_to_city) setOpenJaw(true);
      }
      setLoading(false);
    });
  }, [id, isEdit]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const suggestedRegion = useMemo(() => regionForCountry(f.to_country), [f.to_country]);

  const markExpired = async () => {
    const next = !f.expired;
    await api.patch(`/admin/deals/${id}`, { expired: next });
    set("expired", next);
    toast.success(next ? "DEAL MARKED EXPIRED" : "DEAL REINSTATED");
  };

  const generateBlurb = async () => {
    if (!f.from_city || !f.to_city) {
      toast.error("FROM + TO CITY REQUIRED");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/blurb", {
        from_city: f.from_city, to_city: f.to_city,
        to_country: f.to_country, airline: f.airline,
      });
      set("description", data.description);
      toast.success("INTEL GENERATED");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI FAILED");
    } finally {
      setAiLoading(false);
    }
  };

  const saveDraft = (e) => save(e, { forceDraft: true });

  const save = async (e, { forceDraft = false } = {}) => {
    e.preventDefault();
    if (!forceDraft) {
      if (activeTab === "hotel") {
        if (!f.hotel_name || !f.to_city || !f.price_eur || !f.deal_url) {
          toast.error("Missing required fields");
          return;
        }
      } else if (activeTab === "cruise") {
        if (!f.cruise_line || !f.price_eur || !f.deal_url) {
          toast.error("Missing required fields");
          return;
        }
      } else if (activeTab === "package") {
        if (!f.to_city || !f.price_eur || !f.deal_url) {
          toast.error("Missing required fields");
          return;
        }
      } else {
        if (!f.from_city || !f.to_city || !f.price_eur || !f.airline || !f.travel_window || !f.deal_url) {
          toast.error("Missing required fields");
          return;
        }
      }
    }
    setSaving(true);
    const trimCity = (s) => (s || "").split("(")[0].trim();
    const chosenRegion = f.region || suggestedRegion;
    const payload = {
      ...f,
      deal_type: activeTab,
      from_city: trimCity(f.from_city),
      to_city: trimCity(f.to_city),
      return_from_city: trimCity(f.return_from_city),
      return_to_city: trimCity(f.return_to_city),
      price_eur: parseInt(f.price_eur || 0, 10),
      price_per_night: f.price_per_night ? parseFloat(f.price_per_night) : null,
      price_per_person: f.price_per_person ? parseFloat(f.price_per_person) : null,
      duration_nights: f.duration_nights ? parseInt(f.duration_nights, 10) : null,
      tags: chosenRegion ? [chosenRegion] : [],
      ...(forceDraft ? { published: false } : {}),
    };
    delete payload.region;
    try {
      let deal;
      if (isEdit) {
        console.log('Saving cabin_class:', f.cabin_class);
        await api.patch(`/admin/deals/${id}`, payload);
        toast.success("FILE UPDATED");
        navigate("/admin");
        return;
      }
      const { data } = await api.post("/admin/deals", payload);
      deal = data;
      if (forceDraft) {
        toast.success("DRAFT SAVED");
        navigate(`/admin/edit/${deal.id}`);
        return;
      }
      toast.success(deal.published ? "FILE PUBLISHED" : "DRAFT SAVED");
      // Optionally dispatch newsletter
      if (sendNewsletter && deal.published) {
        try {
          const sendRes = await api.post(`/admin/newsletter/send/${deal.id}`, {}, { timeout: 30000 });
          const d = sendRes.data;
          if (d.status === "queued") {
            toast.success(`// DISPATCH QUEUED FOR ${d.count} SUBSCRIBERS`);
          } else {
            toast.success(`DISPATCHED TO ${d.sent} OPERATORS${d.errors ? ` · ${d.errors} ERRORS` : ""}`);
            deal = { ...deal, sent_count: d.sent };
          }
        } catch (err) {
          if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
            toast.success("// DISPATCH QUEUED — emails are sending");
          } else {
            toast.error("NEWSLETTER DISPATCH FAILED — check Resend logs");
          }
        }
      }
      setPostPublish({ deal });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "SAVE FAILED");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-xs font-mono text-[#9A8F83] tracking-widest uppercase">// loading…</p>;
  }

  const sendTestDispatch = async (dealId) => {
    try {
      const { data } = await api.post(`/admin/newsletter/send-test/${dealId}`);
      toast.success(`TEST DISPATCHED TO deals@restricteddeals.com (${data.status})`);
    } catch {
      toast.error("TEST DISPATCH FAILED");
    }
  };

  const sendTestDispatchPreview = async () => {
    try {
      const { data } = await api.post("/admin/newsletter/send-test-preview", {
        ...f,
        deal_type: activeTab,
      });
      toast.success(`TEST DISPATCHED TO ${data.to} (${data.status})`);
    } catch {
      toast.error("TEST DISPATCH FAILED");
    }
  };

  if (postPublish) return (
    <div className="max-w-2xl">
      <ShareSheet
        deal={postPublish.deal}
        onBack={() => navigate("/admin")}
        onTestDispatch={() => sendTestDispatch(postPublish.deal.id)}
      />
    </div>
  );

  if (showShare) return (
    <div className="max-w-2xl pb-20">
      <ShareSheet
        deal={{ ...f, id, deal_type: activeTab }}
        onBack={() => setShowShare(false)}
        onTestDispatch={sendTestDispatchPreview}
      />
    </div>
  );

  return (
    <div className="max-w-4xl pb-28 md:pb-20" data-testid="admin-deal-form">
      {/* Deal type tab bar */}
      <div className="flex border-b border-[#2A2A2A] mb-8 overflow-x-auto">
        {["flight", "hotel", "cruise", "package"].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => !isEdit && setActiveTab(tab)}
            className={`px-5 py-3 text-xs font-mono tracking-[0.18em] uppercase transition-colors border-b-2 -mb-px whitespace-nowrap shrink-0 ${
              activeTab === tab
                  ? "border-[#CC1A1A] text-white"
                  : "border-transparent text-[#555555] hover:text-white"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

      {activeTab === "hotel" ? (
        <HotelForm f={f} set={set} saving={saving} onSave={save} onCancel={() => navigate("/admin")}
          sendNewsletter={sendNewsletter} setSendNewsletter={setSendNewsletter}
          subStats={subStats} previewOpen={previewOpen} setPreviewOpen={setPreviewOpen}
          dealType={activeTab} region={f.region || suggestedRegion} isEdit={isEdit}
          onTestDispatch={isEdit ? () => sendTestDispatch(id) : null}
          onSaveDraft={saveDraft}
          onTestDispatchPreview={sendTestDispatchPreview}
          onShowShare={isEdit ? () => setShowShare(true) : undefined}
          onMarkExpired={markExpired} onPreviewEmail={() => openServerEmailPreview(id)} />
      ) : activeTab === "cruise" ? (
        <CruiseForm f={f} set={set} saving={saving} onSave={save} onCancel={() => navigate("/admin")}
          sendNewsletter={sendNewsletter} setSendNewsletter={setSendNewsletter}
          subStats={subStats} previewOpen={previewOpen} setPreviewOpen={setPreviewOpen}
          dealType={activeTab} region={f.region || suggestedRegion} isEdit={isEdit}
          onTestDispatch={isEdit ? () => sendTestDispatch(id) : null}
          onSaveDraft={saveDraft}
          onTestDispatchPreview={sendTestDispatchPreview}
          onShowShare={isEdit ? () => setShowShare(true) : undefined}
          onMarkExpired={markExpired} onPreviewEmail={() => openServerEmailPreview(id)} />
      ) : activeTab === "package" ? (
        <PackageForm f={f} set={set} saving={saving} onSave={save} onCancel={() => navigate("/admin")}
          sendNewsletter={sendNewsletter} setSendNewsletter={setSendNewsletter}
          subStats={subStats} previewOpen={previewOpen} setPreviewOpen={setPreviewOpen}
          dealType={activeTab} region={f.region || suggestedRegion} isEdit={isEdit}
          onTestDispatch={isEdit ? () => sendTestDispatch(id) : null}
          onSaveDraft={saveDraft}
          onTestDispatchPreview={sendTestDispatchPreview}
          onShowShare={isEdit ? () => setShowShare(true) : undefined}
          onMarkExpired={markExpired} onPreviewEmail={() => openServerEmailPreview(id)} />
      ) : (
      <>
      <header className="mb-8">
        <p className="eyebrow eyebrow-red">// {isEdit ? "EDIT" : "COMPOSE"}</p>
        <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <h1 className="font-mono text-3xl md:text-5xl text-white uppercase tracking-tight">
            {isEdit ? "Edit file" : "New file"}
          </h1>
          {isEdit && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={markExpired}
                className={`text-[11px] font-mono tracking-[0.2em] uppercase px-3 py-2 transition-colors border ${f.expired ? "border-[#9A8F83] text-[#9A8F83] hover:border-white hover:text-white" : "border-[#CC1A1A] text-[#CC1A1A] hover:bg-[#CC1A1A] hover:text-white"}`}
                data-testid="mark-expired-btn"
              >
                {f.expired ? "↺ REINSTATE" : "⊘ MARK EXPIRED"}
              </button>
              <button
                type="button"
                onClick={() => openServerEmailPreview(id)}
                className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#CC1A1A] hover:text-white border border-[#CC1A1A] hover:border-white px-3 py-2 transition-colors"
                data-testid="newsletter-preview-btn"
              >
                ↗ PREVIEW EMAIL
              </button>
              <button
                type="button"
                onClick={() => setShowShare(true)}
                className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#CC1A1A] hover:text-white border border-[#CC1A1A] hover:border-white px-3 py-2 transition-colors"
                data-testid="share-deal-btn"
              >
                📤 SHARE
              </button>
            </div>
          )}
        </div>
      </header>

      <form onSubmit={save} className="space-y-10">
        {/* Route */}
        <Section title="// ROUTE">
          <AirportAutocomplete
            label="FROM"
            required
            testId="from"
            values={{ city: f.from_city, country: f.from_country, iata: f.from_airport }}
            onChange={(v) => setF((s) => ({ ...s, from_city: v.city, from_country: v.country, from_airport: v.iata }))}
          />
          <AirportAutocomplete
            label="TO"
            required
            testId="to"
            values={{ city: f.to_city, country: f.to_country, iata: f.to_airport }}
            onChange={(v) => setF((s) => ({ ...s, to_city: v.city, to_country: v.country, to_airport: v.iata }))}
          />

          {/* Open-jaw toggle */}
          <button
            type="button"
            onClick={() => setOpenJaw((v) => !v)}
            className="flex items-center gap-2 text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white transition-colors mt-1"
            data-testid="open-jaw-toggle"
          >
            {openJaw
              ? <ChevronDown className="w-3 h-3" />
              : <ChevronRight className="w-3 h-3" />}
            {openJaw ? "− Remove open-jaw return route" : "+ Add open-jaw return route"}
          </button>

          {openJaw && (
            <div className="space-y-5 border-l-2 border-[#CC1A1A] pl-4 mt-1">
              <AirportAutocomplete
                label="RETURN FROM (IF DIFFERENT)"
                testId="return-from"
                placeholder="e.g. Karachi (KHI)"
                values={{ city: f.return_from_city, country: f.return_from_country, iata: f.return_from_airport }}
                onChange={(v) => setF((s) => ({ ...s, return_from_city: v.city, return_from_country: v.country, return_from_airport: v.iata }))}
              />
              <p className="text-[10px] font-mono tracking-[0.14em] uppercase text-[#888888] -mt-3">
                Origin airport for the return leg
              </p>
              <AirportAutocomplete
                label="RETURN TO (IF DIFFERENT)"
                testId="return-to"
                placeholder="e.g. Amsterdam (AMS)"
                values={{ city: f.return_to_city, country: f.return_to_country, iata: f.return_to_airport }}
                onChange={(v) => setF((s) => ({ ...s, return_to_city: v.city, return_to_country: v.country, return_to_airport: v.iata }))}
              />
              <p className="text-[10px] font-mono tracking-[0.14em] uppercase text-[#888888] -mt-3">
                Destination airport for the return leg
              </p>
            </div>
          )}
        </Section>

        {/* Region pills */}
        <Section title="// REGION">
          <RegionPills value={f.region} suggested={suggestedRegion} onChange={(v) => set("region", v)} />
        </Section>

        {/* Price + airline */}
        <Section title="// PRICE & AIRLINE">
          <ToggleButtonGroup
            label="TRIP TYPE"
            options={[
              { value: "round_trip", label: "ROUND TRIP" },
              { value: "one_way",    label: "ONE WAY" },
              { value: "multi_city", label: "MULTI-CITY" },
            ]}
            value={f.trip_type}
            onChange={(v) => set("trip_type", v)}
            testId="trip-type"
          />
          <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3 items-end">
            <Field label="PRICE" required type="number" inputMode="numeric" value={f.price_eur} onChange={(v) => set("price_eur", v)} testId="price" />
            <div>
              <label className="ed-label">CURRENCY</label>
              <select
                className="ed-input ed-input-lg w-full"
                value={f.price_currency}
                onChange={(e) => set("price_currency", e.target.value)}
                data-testid="field-currency"
              >
                {CURRENCY_LIST.map(({ code, symbol, label }) => (
                  <option key={code} value={code}>{code} ({symbol}) — {label}</option>
                ))}
              </select>
            </div>
          </div>
          <Field label="AIRLINE" required value={f.airline} onChange={(v) => set("airline", v)} testId="airline" />
        </Section>

        {/* Travel */}
        <Section title="// TRAVEL">
          <Field label="TRAVEL WINDOW" placeholder="MARCH – MAY 2027" required value={f.travel_window} onChange={(v) => set("travel_window", v)} testId="travel-window" />
          <Field label="EXAMPLE DATES" placeholder="MAR 12 – MAR 21" value={f.example_dates} onChange={(v) => set("example_dates", v)} testId="example-dates" />
          <ToggleButtonGroup
            label="STOPS"
            options={["nonstop", "1 stop", "2+ stops"]}
            value={f.stops}
            onChange={(v) => set("stops", v)}
            testId="stops"
          />
          <ToggleButtonGroup
            label="BAGGAGE"
            options={[
              { value: "personal bag only",  label: "PERSONAL BAG" },
              { value: "cabin only",          label: "CABIN ONLY" },
              { value: "cabin + checked",     label: "CABIN + CHECKED" },
            ]}
            value={f.baggage}
            onChange={(v) => set("baggage", v)}
            testId="baggage"
          />
          <ToggleButtonGroup
            label="CABIN CLASS"
            options={[
              { value: "economy",         label: "ECONOMY" },
              { value: "premium_economy", label: "PREMIUM ECO" },
              { value: "business",        label: "BUSINESS" },
              { value: "first",           label: "FIRST CLASS" },
            ]}
            value={f.cabin_class}
            onChange={(v) => set("cabin_class", v)}
            testId="cabin-class"
          />
        </Section>

        {/* Links */}
        <Section title="// LINKS">
          <Field label="DEAL URL (AFFILIATE/TRACKED)" required type="url" inputMode="url" value={f.deal_url} onChange={(v) => set("deal_url", v)} testId="deal-url" />
          <Field label="HOTEL URL (OPTIONAL)" type="url" inputMode="url" value={f.hotel_url} onChange={(v) => set("hotel_url", v)} testId="hotel-url" />
        </Section>

        {/* Photos */}
        <Section title="// PHOTOS">
          <div>
            <p className="ed-label">PRIMARY PHOTO <span className="text-[#CC1A1A]">*</span></p>
            <PhotoPicker
              imageUrl={f.image_url}
              onChange={(v) => set("image_url", v)}
              onCreditChange={(v) => set("photo_credit", v)}
              credit={f.photo_credit}
              suggestedQuery={f.to_city ? `${f.to_city} ${f.to_country || ""}`.trim() : ""}
              hidePreview
            />
            <ImagePositioner
              url={f.image_url}
              position={f.photo_position}
              onChange={(v) => set("photo_position", v)}
            />
          </div>
          <div>
            <p className="ed-label">ADDITIONAL PHOTOS <span className="text-[#555]">(OPTIONAL · MAX 5)</span></p>
            <GalleryPicker gallery={f.gallery || []} onChange={(v) => set("gallery", v)} />
          </div>
        </Section>

        {/* Copy */}
        <Section title="// COPY">
          <Field label="TEASER" placeholder="ONE-LINE HOOK" value={f.teaser} onChange={(v) => set("teaser", v)} testId="teaser" />
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="ed-label !mb-0">INTEL (DESCRIPTION)</span>
              <button
                type="button"
                onClick={generateBlurb}
                disabled={aiLoading}
                className="text-[11px] font-mono tracking-[0.2em] uppercase flex items-center gap-1 text-[#CC1A1A] hover:text-white disabled:opacity-50"
                data-testid="ai-blurb-btn"
              >
                <Sparkles className="w-3 h-3" />{aiLoading ? "GENERATING…" : "AI · GENERATE"}
              </button>
            </div>
            <RichTextEditor
              value={f.description}
              onChange={(v) => set("description", v)}
              testId="description-input"
            />
          </div>
        </Section>

        {/* Publish toggle */}
        <Section title="// PUBLISHING">
          <label className="flex items-center gap-3 cursor-pointer min-h-[48px]" data-testid="published-toggle-label">
            <input
              type="checkbox"
              checked={f.published}
              onChange={(e) => set("published", e.target.checked)}
              className="w-5 h-5 accent-[#CC1A1A]"
              data-testid="published-toggle"
            />
            <span className="text-xs font-mono tracking-[0.18em] uppercase text-white">
              PUBLISH ON SITE
            </span>
          </label>

          {isEdit && (
            <label className="flex items-center gap-3 cursor-pointer min-h-[48px]" data-testid="expired-toggle-label">
              <input
                type="checkbox"
                checked={!!f.expired}
                onChange={markExpired}
                className="w-5 h-5 accent-[#CC1A1A]"
                data-testid="expired-toggle"
              />
              <span className="text-xs font-mono tracking-[0.18em] uppercase text-[#9A8F83]">
                MARK AS EXPIRED — hides from active feed
              </span>
            </label>
          )}

          <div className="mt-2 border border-[#2A2A2A] bg-[#0E0E0E] p-4" data-testid="newsletter-toggle-block">
            {!isEdit && (
              <>
                <label className="flex items-center gap-3 cursor-pointer min-h-[48px]">
                  <input
                    type="checkbox"
                    checked={sendNewsletter}
                    onChange={(e) => setSendNewsletter(e.target.checked)}
                    className="w-5 h-5 accent-[#CC1A1A]"
                    data-testid="newsletter-send-toggle"
                    disabled={!f.published}
                  />
                  <span className="text-xs font-mono tracking-[0.18em] uppercase text-white">
                    SEND TO {subStats.confirmed} SUBSCRIBERS?
                  </span>
                </label>
                <p className="mt-1 ml-8 text-[10px] font-mono tracking-[0.14em] uppercase text-[#888888]">
                  {f.published ? "OFF BY DEFAULT · TOGGLE ON BEFORE PUBLISHING" : "Enable PUBLISH ON SITE to broadcast"}
                </p>
              </>
            )}
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              disabled={!f.from_city || !f.to_city || !f.price_eur}
              className="mt-3 ml-8 text-[11px] font-mono tracking-[0.2em] uppercase text-[#CC1A1A] hover:text-white disabled:opacity-40"
              data-testid="newsletter-preview-btn"
            >
              ↗ PREVIEW EMAIL (APPROX)
            </button>
            {isEdit && (
              <button
                type="button"
                onClick={() => sendTestDispatch(id)}
                className="mt-2 ml-8 block text-[11px] font-mono tracking-[0.2em] uppercase text-[#9A8F83] hover:text-white"
                data-testid="test-dispatch-btn"
              >
                ✉ TEST DISPATCH TO ADMIN
              </button>
            )}
          </div>

        </Section>

        {previewOpen && <EmailPreview form={f} dealType="flight" region={f.region || suggestedRegion} onClose={() => setPreviewOpen(false)} />}

        {/* Sticky publish bar */}
        <div className="sticky-publish">
          <button type="submit" disabled={saving} className="btn-publish" data-testid="save-deal-btn">
            {saving
              ? "PUBLISHING…"
              : isEdit
              ? "SAVE CHANGES →"
              : sendNewsletter && f.published
              ? `PUBLISH & DISPATCH TO ${subStats.confirmed} →`
              : "PUBLISH DEAL →"}
          </button>
          {!isEdit && (
            <button
              type="button"
              onClick={saveDraft}
              disabled={saving}
              className="mt-2 w-full text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white py-2 disabled:opacity-40"
              data-testid="save-draft-btn"
            >
              SAVE DRAFT
            </button>
          )}
          <button
            type="button"
            onClick={sendTestDispatchPreview}
            disabled={saving}
            className="mt-2 w-full text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white py-2 disabled:opacity-40"
            data-testid="test-dispatch-preview-btn"
          >
            ✉ TEST DISPATCH
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin")}
            className="mt-2 w-full text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white py-2"
            data-testid="cancel-btn"
          >
            CANCEL
          </button>
        </div>
      </form>
      </>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <p className="eyebrow eyebrow-red mb-4">{title}</p>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function EditHeader({ label, isEdit, expired, onMarkExpired, onPreviewEmail }) {
  return (
    <header className="mb-8">
      <p className="eyebrow eyebrow-red">// {isEdit ? "EDIT" : "COMPOSE"}</p>
      <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <h1 className="font-mono text-3xl md:text-5xl text-white uppercase tracking-tight">
          {isEdit ? "Edit file" : label}
        </h1>
        {isEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onMarkExpired}
              className={`text-[11px] font-mono tracking-[0.2em] uppercase px-3 py-2 transition-colors border ${expired ? "border-[#9A8F83] text-[#9A8F83] hover:border-white hover:text-white" : "border-[#CC1A1A] text-[#CC1A1A] hover:bg-[#CC1A1A] hover:text-white"}`}
              data-testid="mark-expired-btn"
            >
              {expired ? "↺ REINSTATE" : "⊘ MARK EXPIRED"}
            </button>
            <button
              type="button"
              onClick={onPreviewEmail}
              className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#CC1A1A] hover:text-white border border-[#CC1A1A] hover:border-white px-3 py-2 transition-colors"
              data-testid="newsletter-preview-btn"
            >
              ↗ PREVIEW EMAIL
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function CurrencySelect({ value, onChange }) {
  return (
    <div>
      <label className="ed-label">CURRENCY</label>
      <select className="ed-input ed-input-lg w-full" value={value} onChange={(e) => onChange(e.target.value)}>
        {CURRENCY_LIST.map(({ code, symbol, label }) => (
          <option key={code} value={code}>{code} ({symbol}) — {label}</option>
        ))}
      </select>
    </div>
  );
}

function PublishBar({ saving, published, onPublishedChange, onCancel, sendNewsletter, setSendNewsletter, subStats, previewOpen, setPreviewOpen, form, dealType, region, isEdit, onTestDispatch, onSaveDraft, onTestDispatchPreview, onShowShare, expired, onMarkExpired }) {
  return (
    <>
      <Section title="// PUBLISHING">
        <label className="flex items-center gap-3 cursor-pointer min-h-[48px]" data-testid="published-toggle-label">
          <input
            type="checkbox"
            checked={published}
            onChange={(e) => onPublishedChange(e.target.checked)}
            className="w-5 h-5 accent-[#CC1A1A]"
            data-testid="published-toggle"
          />
          <span className="text-xs font-mono tracking-[0.18em] uppercase text-white">PUBLISH ON SITE</span>
        </label>

        {isEdit && onMarkExpired && (
          <label className="flex items-center gap-3 cursor-pointer min-h-[48px]" data-testid="expired-toggle-label">
            <input
              type="checkbox"
              checked={!!expired}
              onChange={onMarkExpired}
              className="w-5 h-5 accent-[#CC1A1A]"
              data-testid="expired-toggle"
            />
            <span className="text-xs font-mono tracking-[0.18em] uppercase text-[#9A8F83]">
              MARK AS EXPIRED — hides from active feed
            </span>
          </label>
        )}

        <div className="mt-2 border border-[#2A2A2A] bg-[#0E0E0E] p-4" data-testid="newsletter-toggle-block">
          {!isEdit && (
            <>
              <label className="flex items-center gap-3 cursor-pointer min-h-[48px]">
                <input
                  type="checkbox"
                  checked={sendNewsletter}
                  onChange={(e) => setSendNewsletter(e.target.checked)}
                  className="w-5 h-5 accent-[#CC1A1A]"
                  data-testid="newsletter-send-toggle"
                  disabled={!published}
                />
                <span className="text-xs font-mono tracking-[0.18em] uppercase text-white">
                  SEND TO {subStats?.confirmed ?? 0} SUBSCRIBERS?
                </span>
              </label>
              <p className="mt-1 ml-8 text-[10px] font-mono tracking-[0.14em] uppercase text-[#888888]">
                {published ? "OFF BY DEFAULT · TOGGLE ON BEFORE PUBLISHING" : "Enable PUBLISH ON SITE to broadcast"}
              </p>
            </>
          )}
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            disabled={!form?.price_eur}
            className="mt-3 ml-8 text-[11px] font-mono tracking-[0.2em] uppercase text-[#CC1A1A] hover:text-white disabled:opacity-40"
            data-testid="newsletter-preview-btn"
          >
            ↗ PREVIEW EMAIL (APPROX)
          </button>
          {isEdit && onTestDispatch && (
            <button
              type="button"
              onClick={onTestDispatch}
              className="mt-2 ml-8 block text-[11px] font-mono tracking-[0.2em] uppercase text-[#9A8F83] hover:text-white"
              data-testid="test-dispatch-btn"
            >
              ✉ TEST DISPATCH TO ADMIN
            </button>
          )}
        </div>
      </Section>

      {previewOpen && <EmailPreview form={form} dealType={dealType} region={region} onClose={() => setPreviewOpen(false)} />}

      <div className="sticky-publish">
        <button type="submit" disabled={saving} className="btn-publish" data-testid="save-deal-btn">
          {saving
            ? "PUBLISHING…"
            : sendNewsletter && published
            ? `PUBLISH & DISPATCH TO ${subStats?.confirmed ?? 0} →`
            : "PUBLISH DEAL →"}
        </button>
        {onSaveDraft && (
          <button
            type="button"
            onClick={onSaveDraft}
            disabled={saving}
            className="mt-2 w-full text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white py-2 disabled:opacity-40"
            data-testid="save-draft-btn"
          >
            SAVE DRAFT
          </button>
        )}
        {onTestDispatchPreview && (
          <button
            type="button"
            onClick={onTestDispatchPreview}
            disabled={saving}
            className="mt-2 w-full text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white py-2 disabled:opacity-40"
            data-testid="test-dispatch-preview-btn"
          >
            ✉ TEST DISPATCH
          </button>
        )}
        {onShowShare && (
          <button
            type="button"
            onClick={onShowShare}
            className="mt-2 w-full text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white py-2"
            data-testid="share-deal-bar-btn"
          >
            📤 SHARE THIS DEAL
          </button>
        )}
        <button type="button" onClick={onCancel} className="mt-2 w-full text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-white py-2">
          CANCEL
        </button>
      </div>
    </>
  );
}

function HotelForm({ f, set, saving, onSave, onCancel, sendNewsletter, setSendNewsletter, subStats, previewOpen, setPreviewOpen, dealType, region, isEdit, onTestDispatch, onSaveDraft, onTestDispatchPreview, onShowShare, onMarkExpired, onPreviewEmail }) {
  return (
    <form onSubmit={onSave} className="space-y-10">
      <EditHeader label="New hotel" isEdit={isEdit} expired={f.expired} onMarkExpired={onMarkExpired} onPreviewEmail={onPreviewEmail} />
      <Section title="// PROPERTY">
        <Field label="HOTEL NAME" required value={f.hotel_name} onChange={(v) => set("hotel_name", v)} testId="hotel-name" />
        <Field label="CITY / DESTINATION" required value={f.to_city} onChange={(v) => set("to_city", v)} testId="to-city" />
        <Field label="TRAVEL PERIOD" placeholder="MAY – SEP 2025" value={f.checkin_period} onChange={(v) => set("checkin_period", v)} testId="checkin-period" />
        <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3 items-end">
          <Field label="PRICE PER NIGHT" required type="number" inputMode="numeric" value={f.price_eur} onChange={(v) => set("price_eur", v)} testId="price" />
          <CurrencySelect value={f.price_currency} onChange={(v) => set("price_currency", v)} />
        </div>
        <Field label="PRICE PER PERSON (IF DOUBLE ROOM)" type="number" inputMode="numeric" value={f.price_per_person} onChange={(v) => set("price_per_person", v)} testId="price-per-person" placeholder="e.g. 22" />
        <ToggleButtonGroup
          label="STAR RATING"
          options={[{ value: "3", label: "3★" }, { value: "4", label: "4★" }, { value: "5", label: "5★" }]}
          value={f.star_rating}
          onChange={(v) => set("star_rating", v)}
          testId="star-rating"
        />
        <ToggleButtonGroup
          label="BOARD TYPE"
          options={[
            { value: "Room only",     label: "ROOM ONLY" },
            { value: "Breakfast",     label: "BREAKFAST" },
            { value: "Half board",    label: "HALF BOARD" },
            { value: "All inclusive", label: "ALL INCLUSIVE" },
          ]}
          value={f.board_type}
          onChange={(v) => set("board_type", v)}
          testId="board-type"
        />
      </Section>

      <Section title="// REGION">
        <RegionPills value={f.region} suggested={null} onChange={(v) => set("region", v)} />
      </Section>

      <Section title="// LINK">
        <Field label="BOOKING URL" required type="url" inputMode="url" value={f.deal_url} onChange={(v) => set("deal_url", v)} testId="deal-url" />
      </Section>

      <Section title="// PHOTOS">
        <div>
          <p className="ed-label">PRIMARY PHOTO <span className="text-[#CC1A1A]">*</span></p>
          <PhotoPicker
            imageUrl={f.image_url}
            onChange={(v) => set("image_url", v)}
            onCreditChange={(v) => set("photo_credit", v)}
            credit={f.photo_credit}
            suggestedQuery={`${f.hotel_name || ""} ${f.to_city || ""}`.trim() || "hotel"}
            hidePreview
          />
          <ImagePositioner
            url={f.image_url}
            position={f.photo_position}
            onChange={(v) => set("photo_position", v)}
          />
        </div>
        <div>
          <p className="ed-label">ADDITIONAL PHOTOS <span className="text-[#555]">(OPTIONAL · MAX 5)</span></p>
          <GalleryPicker gallery={f.gallery || []} onChange={(v) => set("gallery", v)} />
        </div>
      </Section>

      <Section title="// COPY">
        <Field label="TEASER" placeholder="ONE-LINE HOOK" value={f.teaser} onChange={(v) => set("teaser", v)} testId="teaser" />
        <div>
          <label className="ed-label">INTEL (DESCRIPTION)</label>
          <RichTextEditor value={f.description} onChange={(v) => set("description", v)} testId="description-input" />
        </div>
      </Section>

      <PublishBar saving={saving} published={f.published} onPublishedChange={(v) => set("published", v)} onCancel={onCancel}
        sendNewsletter={sendNewsletter} setSendNewsletter={setSendNewsletter}
        subStats={subStats} previewOpen={previewOpen} setPreviewOpen={setPreviewOpen}
        form={f} dealType={dealType} region={region} isEdit={isEdit} onTestDispatch={onTestDispatch} onSaveDraft={onSaveDraft} onTestDispatchPreview={onTestDispatchPreview} onShowShare={onShowShare} expired={f.expired} onMarkExpired={onMarkExpired} />
    </form>
  );
}

function CruiseForm({ f, set, saving, onSave, onCancel, sendNewsletter, setSendNewsletter, subStats, previewOpen, setPreviewOpen, dealType, region, isEdit, onTestDispatch, onSaveDraft, onTestDispatchPreview, onShowShare, onMarkExpired, onPreviewEmail }) {
  return (
    <form onSubmit={onSave} className="space-y-10">
      <EditHeader label="New cruise" isEdit={isEdit} expired={f.expired} onMarkExpired={onMarkExpired} onPreviewEmail={onPreviewEmail} />
      <Section title="// VOYAGE">
        <Field label="CRUISE LINE" required value={f.cruise_line} onChange={(v) => set("cruise_line", v)} testId="cruise-line" />
        <Field label="SHIP NAME (OPTIONAL)" value={f.ship_name} onChange={(v) => set("ship_name", v)} testId="ship-name" />
        <Field label="DEPARTURE PORT" value={f.departure_port} onChange={(v) => set("departure_port", v)} testId="departure-port" />
        <Field label="ROUTE" placeholder="e.g. Barcelona · Palma · Ibiza · Marseille" value={f.cruise_route} onChange={(v) => set("cruise_route", v)} testId="cruise-route" />
        <Field label="DESTINATION PORT" value={f.destination_port} onChange={(v) => set("destination_port", v)} testId="destination-port" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="DURATION (NIGHTS)" type="number" inputMode="numeric" value={f.duration_nights} onChange={(v) => set("duration_nights", v)} testId="duration-nights" />
          <Field label="TRAVEL WINDOW" placeholder="JUN – AUG 2025" value={f.travel_window} onChange={(v) => set("travel_window", v)} testId="travel-window" />
        </div>
        <ToggleButtonGroup
          label="CABIN TYPE"
          options={[
            { value: "Inside",  label: "INSIDE" },
            { value: "Outside", label: "OUTSIDE" },
            { value: "Balcony", label: "BALCONY" },
            { value: "Suite",   label: "SUITE" },
          ]}
          value={f.cabin_type}
          onChange={(v) => set("cabin_type", v)}
          testId="cabin-type"
        />
        <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3 items-end">
          <Field label="PRICE PER PERSON" required type="number" inputMode="numeric" value={f.price_eur} onChange={(v) => set("price_eur", v)} testId="price" />
          <CurrencySelect value={f.price_currency} onChange={(v) => set("price_currency", v)} />
        </div>
      </Section>

      <Section title="// REGION">
        <RegionPills value={f.region} suggested={null} onChange={(v) => set("region", v)} />
      </Section>

      <Section title="// LINK">
        <Field label="BOOKING URL" required type="url" inputMode="url" value={f.deal_url} onChange={(v) => set("deal_url", v)} testId="deal-url" />
      </Section>

      <Section title="// PHOTOS">
        <div>
          <p className="ed-label">PRIMARY PHOTO <span className="text-[#CC1A1A]">*</span></p>
          <PhotoPicker
            imageUrl={f.image_url}
            onChange={(v) => set("image_url", v)}
            onCreditChange={(v) => set("photo_credit", v)}
            credit={f.photo_credit}
            suggestedQuery={f.cruise_route || f.departure_port || "cruise ship"}
            hidePreview
          />
          <ImagePositioner
            url={f.image_url}
            position={f.photo_position}
            onChange={(v) => set("photo_position", v)}
          />
        </div>
        <div>
          <p className="ed-label">ADDITIONAL PHOTOS <span className="text-[#555]">(OPTIONAL · MAX 5)</span></p>
          <GalleryPicker gallery={f.gallery || []} onChange={(v) => set("gallery", v)} />
        </div>
      </Section>

      <Section title="// COPY">
        <Field label="TEASER" placeholder="ONE-LINE HOOK" value={f.teaser} onChange={(v) => set("teaser", v)} testId="teaser" />
        <div>
          <label className="ed-label">INTEL (DESCRIPTION)</label>
          <RichTextEditor value={f.description} onChange={(v) => set("description", v)} testId="description-input" />
        </div>
      </Section>

      <PublishBar saving={saving} published={f.published} onPublishedChange={(v) => set("published", v)} onCancel={onCancel}
        sendNewsletter={sendNewsletter} setSendNewsletter={setSendNewsletter}
        subStats={subStats} previewOpen={previewOpen} setPreviewOpen={setPreviewOpen}
        form={f} dealType={dealType} region={region} isEdit={isEdit} onTestDispatch={onTestDispatch} onSaveDraft={onSaveDraft} onTestDispatchPreview={onTestDispatchPreview} onShowShare={onShowShare} expired={f.expired} onMarkExpired={onMarkExpired} />
    </form>
  );
}

function PackageForm({ f, set, saving, onSave, onCancel, sendNewsletter, setSendNewsletter, subStats, previewOpen, setPreviewOpen, dealType, region, isEdit, onTestDispatch, onSaveDraft, onTestDispatchPreview, onShowShare, onMarkExpired, onPreviewEmail }) {
  return (
    <form onSubmit={onSave} className="space-y-10">
      <EditHeader label="New package" isEdit={isEdit} expired={f.expired} onMarkExpired={onMarkExpired} onPreviewEmail={onPreviewEmail} />
      <Section title="// ITINERARY">
        <div className="grid grid-cols-2 gap-3">
          <Field label="ORIGIN" value={f.from_city} onChange={(v) => set("from_city", v)} testId="from-city" placeholder="e.g. London" />
          <Field label="DESTINATION" required value={f.to_city} onChange={(v) => set("to_city", v)} testId="to-city" placeholder="e.g. Bali" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="DURATION (NIGHTS)" type="number" inputMode="numeric" value={f.duration_nights} onChange={(v) => set("duration_nights", v)} testId="duration-nights" />
          <Field label="TRAVEL WINDOW" placeholder="JUL – SEP 2025" value={f.travel_window} onChange={(v) => set("travel_window", v)} testId="travel-window" />
        </div>
        <ToggleButtonGroup
          label="INCLUDES"
          options={[
            { value: "Flight+Hotel",             label: "FLIGHT + HOTEL" },
            { value: "Hotel only",               label: "HOTEL ONLY" },
            { value: "Flight+Hotel+Transfers",   label: "FLIGHT + HOTEL + TRANSFERS" },
          ]}
          value={f.package_includes}
          onChange={(v) => set("package_includes", v)}
          testId="package-includes"
        />
        <Field label="AIRLINE (OPTIONAL)" value={f.airline} onChange={(v) => set("airline", v)} testId="airline" />
        <Field label="HOTEL NAME (OPTIONAL)" value={f.hotel_name} onChange={(v) => set("hotel_name", v)} testId="hotel-name" />
        <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3 items-end">
          <Field label="PRICE PER PERSON" required type="number" inputMode="numeric" value={f.price_eur} onChange={(v) => set("price_eur", v)} testId="price" />
          <CurrencySelect value={f.price_currency} onChange={(v) => set("price_currency", v)} />
        </div>
      </Section>

      <Section title="// REGION">
        <RegionPills value={f.region} suggested={null} onChange={(v) => set("region", v)} />
      </Section>

      <Section title="// LINK">
        <Field label="BOOKING URL" required type="url" inputMode="url" value={f.deal_url} onChange={(v) => set("deal_url", v)} testId="deal-url" />
      </Section>

      <Section title="// PHOTOS">
        <div>
          <p className="ed-label">PRIMARY PHOTO <span className="text-[#CC1A1A]">*</span></p>
          <PhotoPicker
            imageUrl={f.image_url}
            onChange={(v) => set("image_url", v)}
            onCreditChange={(v) => set("photo_credit", v)}
            credit={f.photo_credit}
            suggestedQuery={`${f.to_city || ""} ${f.from_city || ""}`.trim() || "travel package"}
            hidePreview
          />
          <ImagePositioner
            url={f.image_url}
            position={f.photo_position}
            onChange={(v) => set("photo_position", v)}
          />
        </div>
        <div>
          <p className="ed-label">ADDITIONAL PHOTOS <span className="text-[#555]">(OPTIONAL · MAX 5)</span></p>
          <GalleryPicker gallery={f.gallery || []} onChange={(v) => set("gallery", v)} />
        </div>
      </Section>

      <Section title="// COPY">
        <Field label="TEASER" placeholder="ONE-LINE HOOK" value={f.teaser} onChange={(v) => set("teaser", v)} testId="teaser" />
        <div>
          <label className="ed-label">INTEL (DESCRIPTION)</label>
          <RichTextEditor value={f.description} onChange={(v) => set("description", v)} testId="description-input" />
        </div>
      </Section>

      <PublishBar saving={saving} published={f.published} onPublishedChange={(v) => set("published", v)} onCancel={onCancel}
        sendNewsletter={sendNewsletter} setSendNewsletter={setSendNewsletter}
        subStats={subStats} previewOpen={previewOpen} setPreviewOpen={setPreviewOpen}
        form={f} dealType={dealType} region={region} isEdit={isEdit} onTestDispatch={onTestDispatch} onSaveDraft={onSaveDraft} onTestDispatchPreview={onTestDispatchPreview} onShowShare={onShowShare} expired={f.expired} onMarkExpired={onMarkExpired} />
    </form>
  );
}

function Field({ label, required, type = "text", inputMode, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="ed-label">
        {label}{required && <span className="text-[#CC1A1A]"> *</span>}
      </label>
      <input
        type={type}
        inputMode={inputMode}
        value={value ?? ""}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="ed-input ed-input-lg"
        data-testid={`field-${testId}`}
      />
    </div>
  );
}

function EmailPreview({ form, dealType = "flight", region, onClose }) {
  const s = ({ EUR: "€", GBP: "£", USD: "$", AED: "د.إ", PKR: "₨" })[form.price_currency] || "€";
  const price = parseInt(form.price_eur || 0, 10);
  const pricePerPerson = form.price_per_person ? parseFloat(form.price_per_person) : null;
  const img = form.image_url || "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?w=1200&q=80";

  const hotelPrice = pricePerPerson ? `${s}${pricePerPerson}` : `${s}${price}`;
  const hotelPriceLabel = pricePerPerson ? "P.P./NIGHT" : "PER ROOM/NIGHT";

  const subject =
    dealType === "hotel"
      ? `▮ RESTRICTED DEAL — ${form.hotel_name || form.to_city} in ${form.to_city || ""} from ${hotelPrice}/night`
      : dealType === "cruise"
      ? `▮ RESTRICTED DEAL — ${form.cruise_line || "Cruise"} cruise from ${s}${price} p.p.`
      : dealType === "package"
      ? `▮ RESTRICTED DEAL — ${form.from_city || ""} → ${form.to_city || ""} package from ${s}${price} p.p.`
      : `▮ RESTRICTED DEAL — ${form.from_city} → ${form.to_city} for ${s}${price}`;

  const headline =
    dealType === "hotel"
      ? (form.hotel_name || form.to_city || "Hotel Deal")
      : dealType === "cruise"
      ? (form.cruise_line || "Cruise")
      : `${form.from_city || "?"} → ${form.to_city || "?"}`;

  const priceTag =
    dealType === "hotel" ? `${hotelPrice} ${hotelPriceLabel}`
    : dealType === "cruise" || dealType === "package" ? `${s}${price} P.P.`
    : `${s}${price} RT`;

  const details =
    dealType === "hotel"
      ? [
          form.board_type      && { label: "Board",         value: form.board_type },
          form.star_rating     && { label: "Stars",         value: `${form.star_rating}★` },
          form.checkin_period  && { label: "Travel period", value: form.checkin_period },
          form.to_city         && { label: "Location",      value: form.to_city },
        ].filter(Boolean)
      : dealType === "cruise"
      ? [
          form.cruise_line     && { label: "Cruise line",   value: form.cruise_line },
          form.departure_port  && { label: "Departs",       value: form.departure_port },
          form.duration_nights && { label: "Duration",      value: `${form.duration_nights} nights` },
          form.cabin_type      && { label: "Cabin",         value: form.cabin_type },
          form.travel_window   && { label: "Travel window", value: form.travel_window },
        ].filter(Boolean)
      : dealType === "package"
      ? [
          form.package_includes && { label: "Includes",     value: form.package_includes },
          form.duration_nights  && { label: "Duration",     value: `${form.duration_nights} nights` },
          form.airline          && { label: "Airline",      value: form.airline },
          form.travel_window    && { label: "Travel window",value: form.travel_window },
        ].filter(Boolean)
      : [
          { label: "Airline",       value: form.airline },
          { label: "Travel window", value: form.travel_window },
          { label: "Stops",         value: form.stops },
          { label: "Baggage",       value: form.baggage },
        ].filter((d) => d.value);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-10 overflow-y-auto"
      data-testid="email-preview-modal"
      onClick={onClose}
    >
      <div
        className="max-w-[640px] w-full bg-[#111111] border border-[#CC1A1A] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#2A2A2A]">
          <div className="min-w-0">
            <p className="eyebrow eyebrow-red">// EMAIL PREVIEW</p>
            <p className="font-mono text-xs text-[#9A8F83] mt-1 truncate">Subject: {subject}</p>
          </div>
          <button
            onClick={onClose}
            className="btn-rd-outline !py-1 !px-2 !text-[0.6rem] shrink-0 ml-3"
            data-testid="email-preview-close"
          >
            ✕ CLOSE
          </button>
        </div>

        <div className="bg-[#111111]">
          <div className="flex items-center gap-3 px-6 py-8">
            <div className="w-12 h-12 shrink-0 bg-[#CC1A1A] flex items-center justify-center font-mono font-bold text-white">RD</div>
            <div className="leading-none">
              <div className="font-mono text-[9px] tracking-[4px] text-[#9A8F83] lowercase">restricted</div>
              <div className="font-mono text-2xl font-bold text-white mt-1 tracking-tight">DEALS</div>
            </div>
          </div>
          <img src={img} alt="" className="w-full max-h-[280px] object-cover" />
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] border-t-0 px-7 py-8">
            <p className="eyebrow eyebrow-red">// RESTRICTED DEAL</p>
            <p className="font-mono text-[13px] tracking-[4px] text-[#9A8F83] uppercase mt-2">{headline}</p>
            <p className="font-mono text-5xl font-bold text-white leading-none mt-3">{priceTag}</p>
            {form.teaser && <p className="italic text-[#E5E0D8] mt-4 font-sans text-sm">"{form.teaser}"</p>}
            <div className="mt-5 text-sm font-sans text-[#BEB3A6] leading-relaxed">
              {details.map(({ label, value }) => (
                <p key={label}><span className="text-white font-semibold">{label}:</span> {value}</p>
              ))}
            </div>
            <div className="mt-6 bg-[#CC1A1A] text-white font-mono font-bold text-sm tracking-[2px] uppercase py-4 text-center">
              UNLOCK DEAL →
            </div>
          </div>
          <div className="px-6 py-5 border-t border-[#1E1E1E] font-mono text-[11px] tracking-widest uppercase text-[#666] leading-relaxed">
            <span className="text-[#9A8F83] underline">UNSUBSCRIBE</span> · restricteddeals.com<br />© Restricted Deals
          </div>
        </div>
      </div>
    </div>
  );
}
